import type { CoreExpression, Identifier, Literal } from 'jsep';
import jsep from 'jsep';
import { Property } from '../model/entity-type';
import { ExpressionValidationResult } from '../model/expression';
import {
  ReportColumn,
  ReportDefinition,
  ReportParameter,
  aggregateFunctionNames,
  aggregateFunctionResultType,
  compositeProperties,
  identifierRegex,
  reportLimits
} from '../model/report';
import {
  ExpressionReturnType,
  printExpression,
  tryParseExpression
} from './common';
import { parseReportParameter } from './report-parameters';

/*
in-use:
Identifier
Literal
CallExpression
BinaryExpression
UnaryExpression (only negation of literals)
*/

export const sqlDateParts = {
  DATEADD: ['YEAR', 'QUARTER', 'MONTH', 'DAY', 'WEEK'],
  DATEDIFF: ['YEAR', 'QUARTER', 'MONTH', 'DAY', 'WEEK'],
  DATETRUNC: ['YEAR', 'QUARTER', 'MONTH', 'DAY', 'WEEK'],
  DATEPART: ['YEAR', 'QUARTER', 'MONTH', 'DAY', 'WEEK', 'WEEKDAY']
};

const functions: Record<
  string,
  {
    returnType: (
      ...args: { type: ExpressionReturnType | null; node: CoreExpression }[]
    ) => ExpressionReturnType;
    minArguments: number;
    arguments: (
      | {
          type: 'identifier';
          values: string[];
        }
      | {
          type: 'literal';
          validate: (
            value: Literal['value']
          ) => { valid: true } | { valid: false; reason: string };
        }
      | { type: 'value'; valueType: ExpressionReturnType[]; values?: number[] }
    )[];
  }
> = {
  ABS: {
    returnType: num => num.type!,
    minArguments: 1,
    arguments: [{ type: 'value', valueType: ['int', 'decimal'] }]
  },
  DATEADD: {
    returnType: (_part, _num, date) => date.type!,
    minArguments: 3,
    arguments: [
      { type: 'identifier', values: sqlDateParts.DATEADD },
      { type: 'value', valueType: ['int', 'decimal'] },
      { type: 'value', valueType: ['date', 'datetime'] }
    ]
  },
  DATEDIFF: {
    returnType: () => 'int',
    minArguments: 3,
    arguments: [
      { type: 'identifier', values: sqlDateParts.DATEDIFF },
      { type: 'value', valueType: ['date', 'datetime'] },
      { type: 'value', valueType: ['date', 'datetime'] }
    ]
  },
  DATEPART: {
    returnType: () => 'int',
    minArguments: 2,
    arguments: [
      { type: 'identifier', values: sqlDateParts.DATEPART },
      { type: 'value', valueType: ['date', 'datetime'] }
    ]
  },
  DATETRUNC: {
    returnType: () => 'date',
    minArguments: 2,
    arguments: [
      { type: 'identifier', values: sqlDateParts.DATETRUNC },
      { type: 'value', valueType: ['date', 'datetime'] }
    ]
  },
  CEILING: {
    returnType: () => 'int',
    minArguments: 1,
    arguments: [{ type: 'value', valueType: ['int', 'decimal'] }]
  },
  EOMONTH: {
    returnType: () => 'date',
    minArguments: 1,
    arguments: [
      { type: 'value', valueType: ['date', 'datetime'] },
      { type: 'value', valueType: ['int', 'decimal'] }
    ]
  },
  DATE: {
    returnType: () => 'date',
    minArguments: 1,
    arguments: [
      {
        type: 'literal',
        validate: value =>
          typeof value === 'string' &&
          isFinite(Date.parse(value)) &&
          /^\d{4}-\d{1,2}-\d{1,2}$/.test(value)
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected string in the format of "YYYY-MM-DD"`
              }
      }
    ]
  },
  FLOOR: {
    returnType: () => 'int',
    minArguments: 1,
    arguments: [{ type: 'value', valueType: ['int', 'decimal'] }]
  },
  NOW: {
    returnType: () => 'datetime',
    minArguments: 0,
    arguments: []
  },
  ROUND: {
    returnType: (_num, len) =>
      len?.node.type === 'Literal' && len.node.value === 0 ? 'int' : 'decimal',
    minArguments: 1,
    arguments: [
      { type: 'value', valueType: ['int', 'decimal'] },
      { type: 'value', valueType: ['int'], values: [0, 1, 2, 3] }
    ]
  },
  SIGN: {
    returnType: () => 'int',
    minArguments: 1,
    arguments: [{ type: 'value', valueType: ['int', 'decimal'] }]
  },
  MIN: {
    returnType: (...args) =>
      args.some(x => x.type === 'decimal') ? 'decimal' : 'int',
    minArguments: 2,
    arguments: [
      { type: 'value', valueType: ['int', 'decimal'] },
      { type: 'value', valueType: ['int', 'decimal'] }
    ]
  },
  MAX: {
    returnType: (...args) =>
      args.some(x => x.type === 'decimal') ? 'decimal' : 'int',
    minArguments: 2,
    arguments: [
      { type: 'value', valueType: ['int', 'decimal'] },
      { type: 'value', valueType: ['int', 'decimal'] }
    ]
  },
  AGE: {
    returnType: () => 'int',
    minArguments: 2,
    arguments: [
      { type: 'value', valueType: ['date', 'datetime'] },
      { type: 'value', valueType: ['date', 'datetime'] }
    ]
  }
};

interface ReportExpressionValidationContext {
  ancestorNames: string[];
  parameters: ReportParameter[];
  columns: ReportColumn[];
  properties: Property[];
  budget: {
    nodes: number;
    parsedExpressions: Map<
      string,
      { valid: true; node: CoreExpression } | { valid: false; reason: string }
    >;
  };
}

export type ReportFormulaComplexityReason =
  'length' | 'depth' | 'references' | 'output';

export function reportFormulaComplexityError(
  reason: ReportFormulaComplexityReason
) {
  return {
    valid: false,
    reason: `Formula is too complex (${reason}).`
  } as const;
}

function validateReportExpression({
  node,
  context,
  depth = 1
}: {
  node: CoreExpression;
  context: ReportExpressionValidationContext;
  depth?: number;
}): ExpressionValidationResult {
  if (depth > reportLimits.expressionMaxExpandedDepth) {
    return reportFormulaComplexityError('depth');
  }
  context.budget.nodes++;
  if (context.budget.nodes > reportLimits.expressionMaxExpandedNodes) {
    return reportFormulaComplexityError('length');
  }

  const expandExpressionReferences = (
    referenceNode: Identifier,
    expression: string
  ) => {
    const name = referenceNode.name.toUpperCase();
    if (
      context.ancestorNames.length >= reportLimits.expressionMaxReferenceDepth
    ) {
      return reportFormulaComplexityError('references');
    }
    let parseResult = context.budget.parsedExpressions.get(expression);
    if (!parseResult) {
      parseResult = tryParseExpression(expression);
      context.budget.parsedExpressions.set(expression, parseResult);
    }
    if (!parseResult.valid) {
      return {
        valid: false,
        reason: `Referenced identifier "${name}" has an invalid expression. ${parseResult.reason}`
      } as ExpressionValidationResult;
    }
    Object.keys(referenceNode).forEach(key => delete referenceNode[key]);
    Object.assign(referenceNode, structuredClone(parseResult.node));
    const newContext = {
      ...context,
      ancestorNames: [...context.ancestorNames, name]
    };
    return validateReportExpression({
      node: referenceNode,
      context: newContext,
      depth
    });
  };

  switch (node.type) {
    case 'BinaryExpression': {
      const { left, right, operator } = node;
      if (['+', '-', '*', '/', '%'].includes(operator)) {
        const leftResult = validateReportExpression({
          node: left as CoreExpression,
          context,
          depth: depth + 1
        });
        if (leftResult.valid === false) {
          return leftResult;
        }
        if (leftResult.type !== 'int' && leftResult.type !== 'decimal') {
          return {
            valid: false,
            reason: `The expression "${printExpression(
              left as CoreExpression,
              []
            )}" cannot be used with the "${operator}" operator because it does not evaluate to a number.`
          };
        }
        const rightResult = validateReportExpression({
          node: right as CoreExpression,
          context,
          depth: depth + 1
        });
        if (rightResult.valid === false) {
          return rightResult;
        }
        if (rightResult.type !== 'int' && rightResult.type !== 'decimal') {
          return {
            valid: false,
            reason: `The expression "${printExpression(
              left as CoreExpression,
              []
            )}" cannot be used with the "${operator}" operator because it does not evaluate to a number.`
          };
        }
        if (
          operator === '/' ||
          leftResult.type === 'decimal' ||
          rightResult.type === 'decimal'
        ) {
          return { valid: true, type: 'decimal' };
        } else {
          return { valid: true, type: leftResult.type };
        }
      }
      return { valid: false, reason: `Invalid expression "${operator}".` };
    }
    case 'CallExpression': {
      const callee = node.callee as CoreExpression;
      if (callee.type !== 'Identifier') {
        return { valid: false, reason: `Invalid expression.` };
      }
      const fn = functions[callee.name.toUpperCase()];
      if (!fn) {
        return { valid: false, reason: `Invalid expression "${callee.name}".` };
      }
      if (fn.minArguments > node.arguments.length) {
        return {
          valid: false,
          reason: `Too few arguments for "${callee.name}" function. Expected ${
            fn.minArguments === fn.arguments.length
              ? fn.minArguments
              : `${fn.minArguments} - ${fn.arguments.length}`
          }.`
        };
      }
      if (node.arguments.length > fn.arguments.length) {
        return {
          valid: false,
          reason: `Too many arguments for "${callee.name}" function. Expected ${
            fn.minArguments === fn.arguments.length
              ? fn.minArguments
              : `${fn.minArguments} - ${fn.arguments.length}`
          }.`
        };
      }
      const argTypes: (ExpressionReturnType | null)[] = [];
      for (let i = 0; i < node.arguments.length; i++) {
        const arg = fn.arguments[i];
        const argNode = node.arguments[i] as CoreExpression;
        if (arg.type === 'identifier') {
          if (
            argNode.type !== 'Identifier' ||
            !arg.values.includes(argNode.name.toUpperCase())
          ) {
            return {
              valid: false,
              reason: `Invalid argument ${i + 1} for ${
                callee.name
              }. Expected ${arg.values.join(', ')}.`
            };
          }
          argTypes.push(null);
        } else if (arg.type === 'literal') {
          if (
            argNode.type === 'UnaryExpression' &&
            argNode.operator === '-' &&
            argNode.argument.type === 'Literal' &&
            typeof argNode.argument.value === 'number' &&
            Number.isFinite(argNode.argument.value)
          ) {
            const result = arg.validate(argNode.argument.value * -1);
            if (!result.valid) {
              return result;
            }
          } else if (argNode.type !== 'Literal') {
            return {
              valid: false,
              reason: `Invalid argument "${
                argNode.raw
              }" in "${printExpression(node)}". Expected a literal value.`
            };
          } else {
            const result = arg.validate(argNode.value);
            if (!result.valid) {
              return result;
            }
          }
        } else if (arg.type === 'value') {
          const result = validateReportExpression({
            node: argNode,
            context,
            depth: depth + 1
          });
          if (result.valid === false) {
            return result;
          }
          if (!arg.valueType.includes(result.type)) {
            return {
              valid: false,
              reason: `Invalid argument ${i + 1} for ${callee.name}. Expected ${
                arg.valueType[0]
              }.`
            };
          }
          if (
            arg.values &&
            (argNode.type !== 'Literal' ||
              !arg.values.includes(argNode.value as number))
          ) {
            return {
              valid: false,
              reason: `Invalid argument ${i + 1} for ${
                callee.name
              }. Expected ${arg.values.join(', ')}.`
            };
          }
          argTypes.push(result.type);
        } else {
          throw new Error(`Unexpected argument type "${(arg as any).type}".`);
        }
      }
      return {
        valid: true,
        type: fn.returnType(
          ...node.arguments.map((node, i) => ({
            node: node as CoreExpression,
            type: argTypes[i]
          }))
        )
      };
    }
    case 'Identifier': {
      const name = node.name.toUpperCase();
      if (context.ancestorNames.includes(name)) {
        return {
          valid: false,
          reason: `Reference to "${name}" creates a circular reference.`
        };
      }
      if (reportColumnIdentifierRegex.test(name)) {
        if (context.ancestorNames.some(x => identifierRegex.test(x))) {
          return {
            valid: false,
            reason: `Column reference "${name}" is invalid. Columns cannot be referenced in a parameter formula.`
          };
        }
        const index = reportColumnIdentifierToIndex(name);
        if (index >= context.columns.length) {
          return {
            valid: false,
            reason: `Invalid column reference "${name}". Column does not exist.`
          };
        }
        const column = context.columns[index];
        if (column.type === 'expression') {
          return expandExpressionReferences(node, column.expression);
        }
        const { type, property } = getColumnType(column, context.properties);
        if (type) {
          return { valid: true, type };
        }
        const descriptor = property
          ? `${
              column.type === 'subquery'
                ? aggregateFunctionNames[column.aggregate] + ' '
                : ''
            }"${property.label}"`
          : `Column ${name}`;
        return {
          valid: false,
          reason: `Invalid column reference "${name}". ${descriptor} is not eligible for use in formulas.`
        };
      }
      if (identifierRegex.test(name)) {
        const parameter = context.parameters.find(x => x.name === name);
        if (!parameter) {
          return {
            valid: false,
            reason: `Invalid parameter reference "${name}".`
          };
        }
        return parseReportParameter({
          parameter,
          properties: context.properties,
          parseExpression: expression =>
            expandExpressionReferences(node, expression)
        });
      }
      return { valid: false, reason: `Invalid expression "${name}".` };
    }
    case 'UnaryExpression': {
      const { operator, argument } = node;
      if (operator !== '-' || argument.type !== 'Literal') {
        return { valid: false, reason: `Invalid expression "${operator}".` };
      }
      const result = validateReportExpression({
        node: argument as CoreExpression,
        context,
        depth: depth + 1
      });
      if (result.valid === false) {
        return result;
      }
      if (result.type !== 'decimal' && result.type !== 'int') {
        return {
          valid: false,
          reason: `Cannot negate ${result.type} expression.`
        };
      }
      return result;
    }
    case 'Literal': {
      const { value, raw } = node;
      if (
        typeof value === 'number' &&
        isFinite(value) &&
        value >= -100000000 &&
        value <= 100000000
      ) {
        return {
          valid: true,
          type: Number.isInteger(value) ? 'int' : 'decimal'
        };
      }
      return { valid: false, reason: `Invalid expression "${raw}".` };
    }
    default:
      return { valid: false, reason: 'Invalid expression.' };
  }
}

export function reportColumnIndexToIdentifier(index: number): string {
  if (index < 0 || index >= reportLimits.columns) {
    throw new RangeError(
      `Report column index ${JSON.stringify(index)} must be between 0 and ${reportLimits.columns}.`
    );
  }
  let s = '';
  index += 1; // convert to 1-based
  while (index > 0) {
    index -= 1;
    s = String.fromCharCode((index % 26) + 65) + s;
    index = Math.floor(index / 26);
  }
  return s;
}

export const reportColumnIdentifierRegex = /^[A-Z]{1,2}$/;

export function reportColumnIdentifierToIndex(name: string): number {
  if (!reportColumnIdentifierRegex.test(name)) {
    throw new Error(
      `Identifier ${JSON.stringify(name)} is not a valid report column identifier.`
    );
  }
  return [...name].reduce((n, c) => n * 26 + (c.charCodeAt(0) & 31), 0) - 1;
}

export function parseAndValidateReportExpression({
  name,
  expression,
  getDefinitionInfo
}: {
  name: string;
  expression: string;
  getDefinitionInfo: () => {
    parameters: ReportParameter[];
    columns: ReportColumn[];
    properties: Property[];
  };
}):
  | {
      valid: true;
      type: ExpressionReturnType;
      node: CoreExpression;
      originalNode: CoreExpression;
    }
  | { valid: false; reason: string } {
  const parseResult = tryParseExpression(expression);
  if (!parseResult.valid) {
    return parseResult;
  }
  const { node } = parseResult;
  const originalNode = structuredClone(node);
  const result = validateReportExpression({
    node,
    context: {
      ancestorNames: [name],
      ...getDefinitionInfo(),
      budget: { nodes: 0, parsedExpressions: new Map() }
    }
  });
  if (!result.valid) {
    return result;
  }
  const permitted = ['int', 'decimal', 'date', 'datetime'];
  if (!permitted.includes(result.type)) {
    return {
      valid: false,
      reason: `Expression results of type ${
        result.type
      } are not supported. Expected: ${permitted.join(', ')}.`
    };
  }
  return { valid: true, node, originalNode, type: result.type };
}

type ReportExpressionComplexity = {
  nodes: number;
  depth: number;
  referenceDepth: number;
};

type ReportExpressionComplexityResult =
  | ({ valid: true } & ReportExpressionComplexity)
  | { valid: false; reason: string };

/**
 * Validates the expansion cost of every retained report formula without
 * needing property metadata. Runtime semantic validation still applies when
 * a formula is compiled.
 */
export function validateReportDefinitionExpressionComplexity(
  definition: ReportDefinition
): { valid: true } | { valid: false; reason: string; path: string } {
  const formulas = [
    ...definition.parameters
      .map((parameter, index) => ({ parameter, index }))
      .filter(
        (
          item
        ): item is {
          parameter: Extract<ReportParameter, { type: 'expression' }>;
          index: number;
        } => item.parameter.type === 'expression'
      )
      .map(({ parameter, index }) => ({
        name: parameter.name.toUpperCase(),
        expression: parameter.expression,
        path: `definition/parameters/${index}/expression`
      })),
    ...definition.columns
      .map((column, index) => ({ column, index }))
      .filter(
        (
          item
        ): item is {
          column: Extract<ReportColumn, { type: 'expression' }>;
          index: number;
        } => item.column.type === 'expression'
      )
      .map(({ column, index }) => ({
        name: reportColumnIndexToIdentifier(index),
        expression: column.expression,
        path: `definition/columns/${index}/expression`
      }))
  ];
  const formulasByName = new Map(
    formulas.map(formula => [formula.name, formula])
  );
  const memo = new Map<string, ReportExpressionComplexityResult>();
  const visiting = new Set<string>();

  const check = (
    complexity: ReportExpressionComplexity
  ): ReportExpressionComplexityResult => {
    if (complexity.nodes > reportLimits.expressionMaxExpandedNodes) {
      return {
        ...reportFormulaComplexityError('length')
      };
    }
    if (complexity.depth > reportLimits.expressionMaxExpandedDepth) {
      return {
        ...reportFormulaComplexityError('depth')
      };
    }
    if (complexity.referenceDepth > reportLimits.expressionMaxReferenceDepth) {
      return {
        ...reportFormulaComplexityError('references')
      };
    }
    return { valid: true, ...complexity };
  };

  const analyzeNode = (
    node: CoreExpression
  ): ReportExpressionComplexityResult => {
    switch (node.type) {
      case 'BinaryExpression': {
        const left = analyzeNode(node.left as CoreExpression);
        if (!left.valid) return left;
        const right = analyzeNode(node.right as CoreExpression);
        if (!right.valid) return right;
        return check({
          nodes: 1 + left.nodes + right.nodes,
          depth: 1 + Math.max(left.depth, right.depth),
          referenceDepth: Math.max(left.referenceDepth, right.referenceDepth)
        });
      }
      case 'CallExpression': {
        let nodes = 1;
        let depth = 1;
        let referenceDepth = 0;
        for (const argument of node.arguments) {
          const result = analyzeNode(argument as CoreExpression);
          if (!result.valid) return result;
          nodes += result.nodes;
          depth = Math.max(depth, 1 + result.depth);
          referenceDepth = Math.max(referenceDepth, result.referenceDepth);
        }
        return check({ nodes, depth, referenceDepth });
      }
      case 'Identifier': {
        const referencedFormula = formulasByName.get(node.name.toUpperCase());
        if (!referencedFormula) {
          return { valid: true, nodes: 1, depth: 1, referenceDepth: 0 };
        }
        const referenced = analyzeFormula(referencedFormula.name);
        if (!referenced.valid) return referenced;
        return check({
          // Count the authored reference as work in addition to its expansion.
          nodes: 1 + referenced.nodes,
          depth: referenced.depth,
          referenceDepth: 1 + referenced.referenceDepth
        });
      }
      case 'UnaryExpression': {
        const argument = analyzeNode(node.argument as CoreExpression);
        if (!argument.valid) return argument;
        return check({
          nodes: 1 + argument.nodes,
          depth: 1 + argument.depth,
          referenceDepth: argument.referenceDepth
        });
      }
      case 'Literal':
        return { valid: true, nodes: 1, depth: 1, referenceDepth: 0 };
      default:
        return { valid: false, reason: 'Invalid formula expression.' };
    }
  };

  function analyzeFormula(name: string): ReportExpressionComplexityResult {
    const cached = memo.get(name);
    if (cached) return cached;
    if (visiting.has(name)) {
      return {
        valid: false,
        reason: `Reference to "${name}" creates a circular reference.`
      };
    }
    const formula = formulasByName.get(name)!;
    if (
      formula.expression.trim() === '' &&
      reportColumnIdentifierRegex.test(name)
    ) {
      const empty = {
        valid: true,
        nodes: 1,
        depth: 1,
        referenceDepth: 1
      } as const;
      memo.set(name, empty);
      return empty;
    }
    const parsed = tryParseExpression(formula.expression);
    if (!parsed.valid) {
      return {
        valid: false,
        reason: `Formula "${name}" has an invalid expression. ${parsed.reason}`
      };
    }
    visiting.add(name);
    const result = analyzeNode(parsed.node);
    visiting.delete(name);
    if (!result.valid) return result;
    const checked = check({
      nodes: result.nodes,
      depth: result.depth,
      referenceDepth: Math.max(1, result.referenceDepth)
    });
    memo.set(name, checked);
    return checked;
  }

  for (const formula of formulas) {
    const result = analyzeFormula(formula.name);
    if (!result.valid) {
      return { ...result, path: formula.path };
    }
  }
  return { valid: true };
}

export function tryParseAndTranslateReportExpressionColumnReferences(
  value: string,
  map: Record<number, number | null>
): CoreExpression | null {
  if (value === '') {
    return null;
  }
  let node: CoreExpression;
  try {
    node = jsep(value) as CoreExpression;
  } catch {
    return null;
  }
  if (tryTranslateReportExpressionColumnReferences(node, map)) {
    return node;
  }
  return null;
}

function tryTranslateReportExpressionColumnReferences(
  node: CoreExpression,
  map: Record<number, number | null>
): boolean {
  switch (node.type) {
    case 'BinaryExpression': {
      const { left, right } = node;
      return (
        tryTranslateReportExpressionColumnReferences(
          left as CoreExpression,
          map
        ) &&
        tryTranslateReportExpressionColumnReferences(
          right as CoreExpression,
          map
        )
      );
    }
    case 'CallExpression': {
      let translated = true;
      for (const arg of node.arguments) {
        translated &&= tryTranslateReportExpressionColumnReferences(
          arg as CoreExpression,
          map
        );
      }
      return translated;
    }
    case 'Identifier': {
      const deletedName = 'DELETED';
      const name = node.name.toUpperCase();
      if (reportColumnIdentifierRegex.test(name)) {
        const index = reportColumnIdentifierToIndex(name);
        const new_index = map[index];
        if (new_index === null) {
          node.name = deletedName;
        } else {
          node.name = reportColumnIndexToIdentifier(new_index);
        }
        return true;
      } else {
        return true;
      }
    }
    case 'UnaryExpression':
    case 'Literal':
      return true;
    default:
      return false;
  }
}

export function getColumnType(
  column: ReportColumn,
  properties: Property[]
): {
  type: ExpressionReturnType | null;
  property?: Property;
} {
  const getPropertyType: {
    (property: Property): {
      type: ExpressionReturnType | null;
      property: Property;
    };
  } = (property: Property) => {
    switch (property.type) {
      case 'int':
        return { type: 'int', property };
      case 'currency':
      case 'decimal':
        return { type: 'decimal', property };
      case 'date':
        return { type: 'datetime', property };
      case 'bit':
        return { type: 'boolean', property };
      case 'varchar100':
      case 'varcharmax':
        return { type: 'string', property };
      default:
        return { type: null, property };
    }
  };
  switch (column.type) {
    case 'standard': {
      const property = properties.find(
        p => p.property_id === column.property_id
      );
      if (!property) {
        throw new Error(
          `Unable to find standard property ${column.property_id}.`
        );
      }
      if (
        compositeProperties.includes(property.property_id) ||
        property.choice_type !== 'none'
      ) {
        return { type: null, property };
      }
      return getPropertyType(property);
    }
    case 'expression': {
      return { type: null };
    }
    case 'subquery': {
      if (column.aggregate === 'count') {
        return { type: 'int' };
      }
      const aggregateResultType = aggregateFunctionResultType[column.aggregate];
      switch (aggregateResultType) {
        case 'decimal':
          return { type: 'decimal' };
        case 'int':
          return { type: 'int' };
        case 'inherit-number':
        case 'inherit': {
          const property = properties.find(
            p => p.property_id === column.subquery_property_id
          );
          if (!property) {
            throw new Error(
              `Unable to find subquery property ${column.subquery_property_id}.`
            );
          }
          return getPropertyType(property);
        }
        default:
          throw new Error(
            `Unexpected aggregate result type "${aggregateResultType}" for subquery property ${column.subquery_property_id}.`
          );
      }
    }
    default:
      throw new Error(`Unexpected column type "${(column as any).type}".`);
  }
}
